import {
  Day,
  addDays,
  setDay,
  addMonths,
  subMonths,
  getDaysInMonth,
  lastDayOfMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  startOfWeek,
  lastDayOfWeek,
  isEqual,
  isSameMonth,
} from "date-fns";

const DAYS_PER_WEEK = 7;

export const getNormalizedDay = (date: Date, firstDayOfWeekIndex = 1): Day => {
  return ((DAYS_PER_WEEK - firstDayOfWeekIndex + date.getDay()) %
    DAYS_PER_WEEK) as Day;
};

type WeekdayForms = {
  abbreviation: string;
  full: string;
};

export function generateLocalizedWeekdays(
  locale = "de-DE",
  firstDayOfWeekIndex: Day = 1
): WeekdayForms[] {
  const currentDate = new Date();
  const weekdayAbbreviations: WeekdayForms[] = [];

  // set current Date to nearest Date on provided weekday
  const startDay = setDay(currentDate, firstDayOfWeekIndex);

  for (let i = 0; i < DAYS_PER_WEEK; i += 1) {
    const weekdayDate = addDays(startDay, i);
    weekdayAbbreviations.push({
      abbreviation: weekdayDate.toLocaleDateString(locale, {
        weekday: "short",
      }),
      full: addDays(startDay, i).toLocaleDateString(locale, {
        weekday: "long",
      }),
    });
  }

  return weekdayAbbreviations;
}
export const getLastDaysOfMonth = (
  count: number,
  year: number,
  monthIndex: number
): Date[] => {
  const days: Date[] = [];
  const yearMonthDate = new Date(year, monthIndex);
  const daysInMonth = getDaysInMonth(yearMonthDate);

  const startDate = addDays(yearMonthDate, daysInMonth - count);

  for (let i = 0; i < count; i += 1) {
    days.push(addDays(startDate, i));
  }

  return days;
};

export const getFirstDaysOfMonth = (
  count: number,
  year: number,
  monthIndex: number
): Date[] => {
  const days: Date[] = [];
  const startDate = new Date(year, monthIndex);
  for (let i = 0; i < count; i += 1) {
    days.push(addDays(startDate, i));
  }

  return days;
};

export const getAllDaysInMonth = (year: number, monthIndex: number): Date[] => {
  const days: Date[] = [];
  const startDate = new Date(year, monthIndex);

  for (let i = 0; i < getDaysInMonth(startDate); i += 1) {
    days.push(addDays(startDate, i));
  }

  return days;
};

export const generateCalendarDates = (
  year: number,
  monthIndex: number,
  firstDayOfWeekIndex: Day = 1
): Date[] => {
  const thisMonth = new Date(year, monthIndex);
  const firstWeekdayOfMonth = getNormalizedDay(thisMonth, firstDayOfWeekIndex);
  const lastWeekdayOfMonth = getNormalizedDay(
    lastDayOfMonth(thisMonth),
    firstDayOfWeekIndex
  );
  const nextMonthRestDayCount = 6 - lastWeekdayOfMonth;

  const prevMonth = subMonths(thisMonth, 1);
  const nextMonth = addMonths(thisMonth, 1);

  const prevMonthRestDates = getLastDaysOfMonth(
    firstWeekdayOfMonth,
    prevMonth.getFullYear(),
    prevMonth.getMonth()
  );

  const thisMonthDates = getAllDaysInMonth(year, monthIndex);

  const nextMonthRestDates = getFirstDaysOfMonth(
    nextMonthRestDayCount,
    prevMonth.getFullYear(),
    nextMonth.getMonth()
  );

  return [...prevMonthRestDates, ...thisMonthDates, ...nextMonthRestDates];
};

type DateWithAuxilaryData = {
  date: Date;
  isFirstDayOfMonth: boolean;
  isLastDayOfMonth: boolean;
  isFirstDayOfWeek: boolean;
  isLastDayOfWeek: boolean;
  isCurrentMonthDate: boolean;
};

const generateCalendarAuxilaryData = (
  year: number,
  monthIndex: number,
  dates: Date[],
  firstDayOfWeekIndex: Day = 1
): DateWithAuxilaryData[] => {
  let datesWithAuxilaryData: DateWithAuxilaryData[] = [];
  datesWithAuxilaryData = dates.map((date) => {
    return {
      date,
      isFirstDayOfMonth: isFirstDayOfMonth(date),
      isLastDayOfMonth: isLastDayOfMonth(date),
      isFirstDayOfWeek: isEqual(
        date,
        startOfWeek(date, { weekStartsOn: firstDayOfWeekIndex })
      ),
      isLastDayOfWeek: isEqual(
        date,
        lastDayOfWeek(date, { weekStartsOn: firstDayOfWeekIndex })
      ),
      isCurrentMonthDate: isSameMonth(date, new Date(year, monthIndex)),
    };
  });

  return datesWithAuxilaryData;
};

const splitCalendarDataToWeekRows = (
  dates: DateWithAuxilaryData[]
): DateWithAuxilaryData[][] => {
  const calendarWeekRowCount = Math.floor(dates.length / DAYS_PER_WEEK);

  const calendarWeekRowArray: DateWithAuxilaryData[][] = [];

  for (
    let calendarMonthWeekIndex = 0;
    calendarMonthWeekIndex < calendarWeekRowCount;
    calendarMonthWeekIndex += 1
  ) {
    const calendarWeekRow = dates.slice(
      calendarMonthWeekIndex * DAYS_PER_WEEK,
      calendarMonthWeekIndex * DAYS_PER_WEEK + DAYS_PER_WEEK
    );

    calendarWeekRowArray.push(calendarWeekRow);
  }
  return calendarWeekRowArray;
};

export const generateCalendarData = (
  year: number,
  monthIndex: number,
  firstDayOfWeekIndex: Day = 1
): DateWithAuxilaryData[][] => {
  const dates = generateCalendarDates(year, monthIndex, firstDayOfWeekIndex);
  const datesWithAuxilaryData = generateCalendarAuxilaryData(
    year,
    monthIndex,
    dates,
    firstDayOfWeekIndex
  );

  return splitCalendarDataToWeekRows(datesWithAuxilaryData);
};
